<template>
  <div class="user-page agenda-page">
    <div class="uk-grid-medium" uk-grid>
      <div class="uk-width-1-1">
        <page-header :title="agendaTitle" class="uk-width-1-1@m" icon="uil-calendar-alt"/>

        <div class="agenda-page__filters">
          <div class="agenda-page__filter">
            <label>{{ $t("general.view") }}</label>
            <multiple-select-input v-model="displayPeriodUom"
                                   :allowEmpty="false"
                                   :multiple="false"
                                   :searchable="false"
                                   :showLabels="false"
                                   :values="displayPeriods"
            />
          </div>

          <div class="agenda-page__filter">
            <label>{{ $t("general.type_of_education") }}</label>
            <multiple-select-input v-model="contentType"
                                   :allowEmpty="false"
                                   :multiple="false"
                                   :searchable="false"
                                   :showLabels="false"
                                   :values="contentTypes"
                                   @select="onContentTypeChange"
            />
          </div>
        </div>

        <agenda-calendar :items="items"
                         :loading="calendarLoading"
                         :notification-items="notificationItems"
                         :summary-data="summaryData"
                         :use-custom-language="isCustomLanguage"
                         :view-mode="displayPeriodUom.id"
                         @change-view="onCalendarViewChange"
                         @date-range-change="onDateRangeChange"
                         @item-click="onItemClick"
        />
        <notice-detail-modal ref="noticeModal" />
      </div>
    </div>
  </div>
</template>

<script>
import SecureLS from "secure-ls";
import store from "@/core/services";
import user_module, { MODULE_NAME, GET_USER } from "@/core/services/store/user.module";
import { GET_ITEMS } from "@/core/services/store/REST.module";
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput.vue";
import PageHeader from "@/view/components/common/PageHeader.vue";
import AgendaCalendar from "./components/AgendaCalendar.vue";
import NoticeDetailModal from "@/view/components/modal/NoticeDetailModal.vue";
import i18nService from "@/core/services/i18n.service.js";
import ApiService from "@/core/services/api.service";

const ls = new SecureLS({ encodingType: "aes", isCompression: true });
const MODULE = MODULE_NAME;

const ROUTE_MAP = {
  training: "user.education-detail",
  assignment: "user.assignment-detail",
};

export default {
  name: "Agenda",
  components: {
    MultipleSelectInput,
    PageHeader,
    AgendaCalendar,
    NoticeDetailModal,
  },
  beforeCreate() {
    if (!(store && store.state && store.state[MODULE])) {
      store.registerModule(MODULE, user_module);
    }
  },
  data() {
    return {
      isCustomLanguage: false,
      displayPeriods: [
        { id: "week", name: this.$t("ajadam.week") },
        { id: "month", name: this.$t("ajadam.month") },
        { id: "year", name: this.$t("ajadam.year") },
      ],
      contentTypes: [
        { id: "all", name: this.$t("general.all") },
        { id: "training", name: this.$t("education.catalog_education") },
        { id: "assignment", name: this.$t("education.assignments") },
        { id: "notice", name: this.$t("general.announcements") },
      ],
      displayPeriodUom: { id: "month", name: this.$t("ajadam.month") },
      contentType: { id: "all", name: this.$t("general.all") },
      calendarLoading: false,
      items: [],
      agendaItems: [],
      noticeItems: [],
      notificationItems: [],
      notificationsLoaded: false,
      summaryData: [],
      currentDateRange: { start: null, end: null, year: null },
      apiURL: "api/user/self",
      apiAgendaURL: "api/user/agenda",
      apiSummaryURL: "api/user/agenda/summary",
      menuList: [],
    };
  },
  computed: {
    agendaTitle() {
      const sourceMenu = this.getDashboardMenuItems(this.menuList);
      const menuItem = sourceMenu.find(item => item.type === "agenda");
      return this.getLocalizedWidgetTitle(menuItem, this.$t("sideNav.Agent"), this.$i18n?.locale);
    },
  },
  mounted() {
    const data = ls.get("dashboardMenuList");
    if (data?.menu) {
      this.menuList = data.menu;
    }

    this.isCustomLanguage = i18nService.getActiveLanguage() === "az";
    this.getCurrentUser();
    this.fetchActiveNotices();
  },
  methods: {
    onContentTypeChange() {
      this.loadItems();
    },
    onCalendarViewChange({ mode }) {
      const selected = this.displayPeriods.find(period => period.id === mode);
      if (selected) {
        this.displayPeriodUom = selected;
      }
    },
    onDateRangeChange({ start, end, year, mode }) {
      this.currentDateRange = { start, end, year };
      if (mode === "year") {
        this.fetchSummary(year);
      } else {
        this.fetchItems(start, end);
      }
    },
    onItemClick(item) {
      if (item?.type === "notice") {
        this.$refs.noticeModal.open(item);
        return;
      }
      if (!item?.route || !item?.id) return;
      this.$router.push({ name: item.route, params: { id: item.id } });
    },
    getCurrentUser() {
      this.$store.dispatch(`${MODULE}/${GET_USER}`, {
        url: this.apiURL,
        acceptPromise: true,
      });
    },
    async loadItems() {
      if (this.contentType.id === "training" && !this.CatalogTrainingVisible) {
        this.items = [];
        this.summaryData = [];
        return;
      }

      if (this.displayPeriodUom.id === "year" && this.currentDateRange.year) {
        await this.fetchSummary(this.currentDateRange.year);
      } else if (this.currentDateRange.start && this.currentDateRange.end) {
        await this.fetchItems(this.currentDateRange.start, this.currentDateRange.end);
      }
    },
    async fetchItems(start, end) {
      if (this.contentType.id === "training" && !this.CatalogTrainingVisible) {
        this.items = [];
        return;
      }

      this.calendarLoading = true;
      try {
        if (this.contentType.id === "notice") {
          this.items = this.noticeItems;
          return;
        }

        const filters = { start, end, type: this.contentType.id };
        const result = await this.$store.dispatch(GET_ITEMS, {
          url: this.apiAgendaURL,
          filters,
          acceptPromise: false,
        });

        if (!result?.status) {
          this.items = [];
          return;
        }

        const list = Array.isArray(result.data) ? result.data : (result.data?.data || []);
        const mapped = list.map(item => this.normalizeItem(item));
        this.agendaItems = mapped;
        this.items = this.contentType.id === "all" ? [...mapped, ...this.noticeItems] : mapped;

        if (!this.notificationsLoaded) {
          this.notificationItems = [...mapped, ...this.noticeItems];
          this.notificationsLoaded = true;
        }
      } catch (error) {
        console.error("Agenda load error:", error);
        this.items = [];
      } finally {
        this.calendarLoading = false;
      }
    },
    async fetchSummary(year) {
      if (this.contentType.id === "training" && !this.CatalogTrainingVisible) {
        this.summaryData = [];
        return;
      }

      this.calendarLoading = true;
      try {
        if (this.contentType.id === "notice") {
          this.summaryData = this.getNoticeSummary(year);
          return;
        }

        const filters = { year, type: this.contentType.id };
        const result = await this.$store.dispatch(GET_ITEMS, {
          url: this.apiSummaryURL,
          filters,
          acceptPromise: false,
        });

        if (!result?.status) {
          this.summaryData = this.contentType.id === "all" ? this.getNoticeSummary(year) : [];
          return;
        }

        const summary = Array.isArray(result.data) ? result.data : (result.data?.data || []);
        this.summaryData = this.contentType.id === "all"
          ? this.mergeSummary(summary, this.getNoticeSummary(year))
          : summary;
      } catch (error) {
        console.error("Agenda summary load error:", error);
        this.summaryData = [];
      } finally {
        this.calendarLoading = false;
      }
    },
    normalizeItem(apiItem) {
      return {
        id: apiItem.id,
        type: apiItem.type,
        route: ROUTE_MAP[apiItem.type] || null,
        title: apiItem.title,
        startDate: apiItem.start_date ? new Date(apiItem.start_date) : null,
        endDate: apiItem.end_date ? new Date(apiItem.end_date) : null,
        periodStartDate: apiItem.period_start_date ? new Date(apiItem.period_start_date) : null,
        deadlineDate: apiItem.deadline_date ? new Date(apiItem.deadline_date) : null,
        isExpired: !!apiItem.is_expired,
        isDueSoon: !!apiItem.is_due_soon,
      };
    },
    async fetchActiveNotices() {
      try {
        const response = await ApiService.get("api/user/active-notices");
        const payload = response?.data?.data || response?.data || [];
        const list = Array.isArray(payload) ? payload : (payload.data || []);
        this.noticeItems = list
          .filter(item => item.start_date && item.end_date)
          .map(item => ({
            ...item,
            id: item.id,
            type: "notice",
            route: null,
            title: item.name || item.title || this.$t("general.announcement"),
            startDate: new Date(item.start_date),
            endDate: new Date(item.end_date),
            isExpired: false,
            isDueSoon: false,
          }));

        if (this.contentType.id === "all") {
          this.items = [...this.agendaItems, ...this.noticeItems];
        } else if (this.contentType.id === "notice") {
          this.items = this.noticeItems;
        }
        this.notificationItems = [...this.agendaItems, ...this.noticeItems];

        if (this.displayPeriodUom.id === "year" && this.currentDateRange.year) {
          this.loadItems();
        }
      } catch (error) {
        console.error("Active notices load error:", error);
        this.noticeItems = [];
      }
    },
    getNoticeSummary(year) {
      const counts = {};
      this.noticeItems.forEach(item => {
        const start = new Date(item.startDate);
        const end = new Date(item.endDate);
        for (let month = 0; month < 12; month++) {
          const monthStart = new Date(year, month, 1);
          const monthEnd = new Date(year, month + 1, 0, 23, 59, 59);
          if (start <= monthEnd && end >= monthStart) counts[month + 1] = (counts[month + 1] || 0) + 1;
        }
      });
      return Object.keys(counts).map(month => ({ month: Number(month), count: counts[month] }));
    },
    mergeSummary(base, extra) {
      const counts = {};
      [...base, ...extra].forEach(entry => {
        counts[entry.month] = (counts[entry.month] || 0) + Number(entry.count || 0);
      });
      return Object.keys(counts).map(month => ({ month: Number(month), count: counts[month] }));
    },
  },
};
</script>

<style lang="scss" scoped>
.agenda-page {
  min-width: 0;
  max-width: 100%;
  overflow-x: hidden;
}

.agenda-page__filters {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
  margin-bottom: 16px;
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
}

.agenda-page__filter {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.agenda-page__filter label {
  font-size: 11px;
  font-weight: 800;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.agenda-page__filter :deep(.multiselect),
.agenda-page__filter :deep(.multiple-select-input) {
  width: 100%;
}

@media (max-width: 768px) {
  .agenda-page__filters {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 16px;
  }

  .agenda-page__filter {
    gap: 8px;
  }

  .agenda-page__filter label {
    font-size: 14px;
    line-height: 1.5;
    letter-spacing: 0;
    text-transform: none;
  }

  .agenda-page__filter :deep(.multiselect__tags) {
    min-height: 48px;
    height: 48px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    padding: 12px 40px 12px 16px;
    border-radius: 10px;
  }

  .agenda-page__filter :deep(.multiselect__single) {
    min-width: 0;
    margin: 0;
    font-size: 16px;
    line-height: 1.5;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .agenda-page__filter :deep(.multiselect__option) {
    min-height: 48px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    padding: 12px 16px;
    font-size: 16px;
    line-height: 1.5;
  }
}
</style>
